'use client';

import { useState, useEffect, useMemo } from 'react';
import { Search, Building2, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';

interface VendorInfo {
  vendor: string;
  count: number;
}

interface KEVCatalog {
  count: number;
  vulnerabilities: Array<{
    vendorProject: string;
  }>;
}

export default function VendorIndexClient() {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<KEVCatalog | null>(null);

  // Fetch KEV data on mount
  useEffect(() => {
    async function fetchKEV() {
      try {
        const response = await fetch('/api/tools/cve/kev');
        if (!response.ok) throw new Error('Failed to fetch KEV catalog');
        const data: KEVCatalog = await response.json();
        setCatalog(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchKEV();
  }, []);

  // Calculate vendors from catalog
  const { vendors, totalCVEs } = useMemo(() => {
    if (!catalog) return { vendors: [], totalCVEs: 0 };

    const vendorCounts = new Map<string, number>();
    catalog.vulnerabilities.forEach(v => {
      const vendor = v.vendorProject || 'Unknown';
      vendorCounts.set(vendor, (vendorCounts.get(vendor) || 0) + 1);
    });

    const vendorList = Array.from(vendorCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([vendor, count]) => ({ vendor, count }));

    return { vendors: vendorList, totalCVEs: catalog.count };
  }, [catalog]);

  const filteredVendors = vendors.filter(v =>
    v.vendor.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading vendor data...</span>
      </div>
    );
  }

  // Error state
  if (error || !catalog) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-red-800 dark:text-red-200 mb-2">
          Unable to Load Vendor Data
        </h2>
        <p className="text-red-700 dark:text-red-300">
          {error || 'Vendor data is temporarily unavailable. Please try again later.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{vendors.length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Vendors</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">{totalCVEs}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">KEV CVEs</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 col-span-2 md:col-span-1">
          <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">
            {vendors.length > 0 ? Math.round(totalCVEs / vendors.length * 10) / 10 : 0}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Avg CVEs per Vendor</div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search vendors..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700"
          />
        </div>
      </div>

      {/* Vendor List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold mb-4">
          Vendors with Known Exploited Vulnerabilities
          {searchQuery && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({filteredVendors.length} of {vendors.length})
            </span>
          )}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredVendors.map(({ vendor, count }) => (
            <a
              key={vendor}
              href={`/tools/security/cve-lookup/vendor/${encodeURIComponent(vendor)}`}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <span className="font-medium truncate">{vendor}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  count >= 50 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  count >= 20 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                  count >= 5 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {count} CVE{count !== 1 ? 's' : ''}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </a>
          ))}
        </div>

        {filteredVendors.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No vendors found matching &quot;{searchQuery}&quot;
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">About KEV Vendor Data</h3>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          This page lists vendors with vulnerabilities in CISA&apos;s Known Exploited Vulnerabilities (KEV) catalog.
          These are CVEs that have been confirmed as actively exploited in real-world attacks.
          The data is sourced from the official CISA KEV feed and updated regularly.
        </p>
      </div>
    </div>
  );
}
